import React, { useState } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { Btn, H4, P } from '../../AbstractElements';
import FwLogo from '../../Layout/Sidebar/FwLogo';
import { markPurchaseReturn } from '../../helpers/purchaseReturn';

const getApiBase = () => {
  const base = process.env.REACT_APP_FW_API;
  if (!base) return '';
  return base.replace(/\/$/, '');
};

/**
 * Вход и регистрация покупателя, пришедшего с юбилейного лендинга.
 *
 * Отдельно от обычных вкладок входа по двум причинам. Во-первых, человек
 * приходит сюда сразу после оплаты и должен попасть в кабинет одним движением,
 * без выбора вкладок. Во-вторых, ручки /v1/birthday/* доначисляют награды по
 * оплатам на эту почту — обычный /v1/auth/login этого не делает.
 *
 * Один и тот же компонент обслуживает оба адреса: mode приходит из роутера.
 */
const Birthday = ({ mode = 'login' }) => {
  const isRegister = mode === 'register';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const mail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      setError('Проверьте адрес почты');
      return;
    }
    if (isRegister) {
      if (password.length < 8) {
        setError('Пароль должен быть не короче восьми символов');
        return;
      }
      if (password !== passwordConfirm) {
        setError('Пароли не совпадают');
        return;
      }
    } else if (!password) {
      setError('Введите пароль');
      return;
    }

    setLoading(true);
    try {
      const path = isRegister ? '/v1/birthday/registration-ajax' : '/v1/birthday/login-ajax';
      const res = await fetch(getApiBase() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: mail, password }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || 'Войти не удалось, попробуйте ещё раз');
        return;
      }

      // Ключ именно 'token': под ним токен читают PrivateRoute и все запросы
      // кабинета. Длинный (180 дней) предпочтительнее часового — иначе
      // кабинет разлогинивает посреди работы.
      const token = data.access_token_180 || data.access_token;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('Name', mail);
        localStorage.setItem('login', JSON.stringify(true));
      }

      if (data.redirect && data.access_token) {
        // На hypercube уходит короткий токен: он живёт час, и его достаточно,
        // чтобы один раз войти. Длинный в адресной строке светить незачем.
        window.location.href = `${data.redirect}?token=${encodeURIComponent(data.access_token)}`;
        return;
      }

      // Через эти две страницы приходят только с оплаты: отмечаем возвращение,
      // чтобы профиль встретил человека итогом покупки, а не просто собой.
      markPurchaseReturn();

      // Не на /home, а сразу в профиль: там лежат награды, за которыми человек
      // и платил, и там же всплывает окно с итогом покупки (задача №1908).
      navigate(`${process.env.PUBLIC_URL}/profile`);
    } catch (err) {
      setError('Сервер не отвечает. Попробуйте через минуту.');
    } finally {
      setLoading(false);
    }
  };

  return (
    /* Тот же каркас, что у обычного входа (Auth/Signin.jsx): классы
       auth-login и fw-auth дают кремовый фон и карточку по макету, а
       fw-auth-logo — знак «Волшебного мира» над формой.

       Раньше здесь стоял голый login-card, и страница выглядела экраном
       купленной темы: синий фрактал, фиолетовая кнопка. Человек приходил
       сюда прямо с кремово-золотого лендинга и попадал будто на чужой сайт.

       Вкладок «Регистрация» и «Восстановление» тут нет намеренно: у входа
       для покупателей один сценарий, и переключатель на три пункта только
       уводил бы от него. Ссылка на второй режим — под формой. */
    <div className="login-card auth-login fw-auth">
      <div>
        <div className="logo logo-login fw-auth-logo">
          <FwLogo />
          <span>Волшебный Мир</span>
        </div>
      </div>
      {/* login-main1 — тот же контейнер, что на общем экране входа: под
          .fw-auth он даёт рамку, тень и скругление карточки. Без него
          форма висела бы прямо на фоне. */}
      <div className="login-main1 login-main">
        <Form className="theme-form login-form" onSubmit={handleSubmit}>
          <H4>{isRegister ? 'Создайте кабинет' : 'Вход в кабинет'}</H4>
          <P>
            {isRegister
              ? 'Укажите ту же почту, на которую оформляли лицензию — награды придут сразу.'
              : 'Войдите по почте, которую указывали при оплате.'}
          </P>

          <FormGroup>
            <Label className="col-form-label">Почта</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ваш адрес почты"
              autoComplete="email"
            />
          </FormGroup>

          <FormGroup className="position-relative">
            <Label className="col-form-label">Пароль</Label>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isRegister ? 'Не короче 8 символов' : 'Ваш пароль'}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />
            {/* Подпись рисует тема через CSS у пустого span — свой текст сюда
                класть нельзя, иначе получается «hideПоказать». */}
            <div className="show-hide" onClick={() => setShowPassword(!showPassword)}>
              <span className={showPassword ? '' : 'show'}></span>
            </div>
          </FormGroup>

          {isRegister && (
            <FormGroup>
              <Label className="col-form-label">Повторите пароль</Label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Ещё раз тот же пароль"
                autoComplete="new-password"
              />
            </FormGroup>
          )}

          {error && <P attrP={{ className: 'text-danger' }}>{error}</P>}

          <FormGroup className="mb-0">
            {/* Цвет primary остаётся: под .fw-auth правило .btn-primary уже
                перекрашивает кнопку в золото — то же самое, что на общем
                экране входа. Свой класс тут был бы вторым источником цвета. */}
            <Btn attrBtn={{ color: 'primary', className: 'd-block w-100', disabled: loading }}>
              {loading ? 'Минуту…' : isRegister ? 'Создать кабинет' : 'Войти'}
            </Btn>
          </FormGroup>

          <P attrP={{ className: 'mt-4 mb-0 text-center' }}>
            {isRegister ? 'Кабинет уже есть? ' : 'Ещё нет кабинета? '}
            <a href={`${process.env.PUBLIC_URL}/birthday/${isRegister ? 'login' : 'registration'}`}>
              {isRegister ? 'Войти' : 'Зарегистрироваться'}
            </a>
          </P>
        </Form>
      </div>
    </div>
  );
};

export default Birthday;
